import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Modal from "./Modal";
import { getCumplimientoRutaMaterna } from "../../api/patientApi";
import {
  RutaMaternaContainer,
  TarjetaPaciente,
  IdentidadPacienteBox,
  NombrePaciente,
  DocumentoPaciente,
  MetricasGestacionGrid,
  MetricaCard,
  DatoPaciente,
  EtiquetaDato,
  ValorNumerico,
  BadgeSemanas,
  NotaSinActividades,
  LeyendaContainer,
  LeyendaItem,
  PuntoColor,
  TextoEstadoLeyenda,
  TablaWrapper,
  TablaRuta,
  ThRuta,
  TrRuta,
  TdRuta,
  LoadingBox,
} from "./RutaMaternaModal.styles";

const LEYENDA_ESTADOS = [
  {
    key: "cumplida",
    label: "Cumplida",
    color: "#808080",
    clase: "e-cumplida",
  },
  {
    key: "no_cumplida",
    label: "No cumplida",
    color: "#E97132",
    clase: "e-no-cumplida",
  },
  {
    key: "en_curso",
    label: "Se debe cumplir de inmediato",
    color: "#E97232E4",
    clase: "e-en-curso",
  },
  {
    key: "pendiente",
    label: "Aún no ha llegado la fecha de cumplimiento",
    color: "#B9B9B9",
    clase: "e-pendiente",
  },
];

const RutaMaternaModal = ({ isOpen, onClose, autoid }) => {
  const [loading, setLoading] = useState(false);
  const [informe, setInforme] = useState(null);

  useEffect(() => {
    if (!isOpen || !autoid) {
      setInforme(null);
      return;
    }

    let active = true;
    const cargarInforme = async () => {
      try {
        setLoading(true);
        const res = await getCumplimientoRutaMaterna(autoid);
        if (!active) return;

        if (res?.success) {
          setInforme(res.data);
        } else {
          toast.error(res?.message || "No fue posible obtener el cumplimiento de la ruta materna.");
          setInforme(null);
        }
      } catch (error) {
        if (!active) return;
        const msg =
          error?.response?.data?.message ||
          "Error al consultar el cumplimiento de la ruta materna.";
        toast.error(msg);
        setInforme(null);
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    cargarInforme();

    return () => {
      active = false;
    };
  }, [isOpen, autoid]);

  const encabezado = informe?.encabezado;
  const lineas = informe?.lineas || [];
  const sinActividades = informe?.sin_actividades;
  const mensajeSinActividades =
    informe?.mensaje || "La paciente no registra ninguna actividad en el sistema";

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Ruta materna"
      width="1080px"
      maxH="88vh"
      minH="320px"
      padding="20px"
      bodyOverflow="auto"
      closeOnOverlay={true}
    >
      <RutaMaternaContainer>
        {loading ? (
          <LoadingBox>Cargando cumplimiento de la ruta materna...</LoadingBox>
        ) : !informe || sinActividades || !encabezado ? (
          <TarjetaPaciente>
            <NotaSinActividades>{mensajeSinActividades}</NotaSinActividades>
          </TarjetaPaciente>
        ) : (
          <>
            <TarjetaPaciente>
              <IdentidadPacienteBox>
                <NombrePaciente>{encabezado.nombre}</NombrePaciente>
                <DocumentoPaciente>
                  {`${encabezado.tipo_id || ""} ${encabezado.identificacion || ""}`.trim()}
                </DocumentoPaciente>
              </IdentidadPacienteBox>

              <MetricasGestacionGrid>
                <MetricaCard>
                  <EtiquetaDato>Fecha de ingreso al programa</EtiquetaDato>
                  <DatoPaciente>
                    <ValorNumerico>{encabezado.fecha_ingreso}</ValorNumerico>
                    <BadgeSemanas>{encabezado.semanas_al_ingr} semanas</BadgeSemanas>
                  </DatoPaciente>
                </MetricaCard>

                <MetricaCard>
                  <EtiquetaDato>FUM (Edad gestacional actual)</EtiquetaDato>
                  <DatoPaciente>
                    <ValorNumerico>{encabezado.fum}</ValorNumerico>
                    <BadgeSemanas>{encabezado.egp} semanas</BadgeSemanas>
                  </DatoPaciente>
                </MetricaCard>
              </MetricasGestacionGrid>
            </TarjetaPaciente>

            <LeyendaContainer>
              {LEYENDA_ESTADOS.map((item) => (
                <LeyendaItem key={item.key}>
                  <PuntoColor $bg={item.color} />
                  <TextoEstadoLeyenda className={item.clase}>
                    {item.label}
                  </TextoEstadoLeyenda>
                </LeyendaItem>
              ))}
            </LeyendaContainer>

            <TablaWrapper>
              <TablaRuta>
                <thead>
                  <tr>
                    <ThRuta className="col-trim">Trimestre</ThRuta>
                    <ThRuta className="col-sem">Semana</ThRuta>
                    <ThRuta>Actividad</ThRuta>
                    <ThRuta className="col-fecha">Fecha</ThRuta>
                    <ThRuta className="col-res">Resultado</ThRuta>
                  </tr>
                </thead>
                <tbody>
                  {lineas.map((linea, idx) => (
                    <TrRuta
                      key={`${linea.orden || idx}-${linea.semana || 0}-${idx}`}
                      className={linea.clase}
                    >
                      <TdRuta className="num col-trim">
                        {linea.trimestre === null || linea.trimestre === undefined
                          ? ""
                          : linea.trimestre}
                      </TdRuta>
                      <TdRuta className="num col-sem">
                        {linea.semana === null || linea.semana === undefined
                          ? ""
                          : linea.semana}
                      </TdRuta>
                      <TdRuta className="actividad">{linea.actividad}</TdRuta>
                      <TdRuta className="num col-fecha">{linea.fecha}</TdRuta>
                      <TdRuta className="col-res">{linea.resultado}</TdRuta>
                    </TrRuta>
                  ))}
                </tbody>
              </TablaRuta>
            </TablaWrapper>
          </>
        )}
      </RutaMaternaContainer>
    </Modal>
  );
};

export default RutaMaternaModal;

